/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { InfoPopover } from "components/InfoPopover"
import useResponsive from "hooks/useResponsive"
import React, { useMemo } from "react"
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { formatCurrency } from "services/computations/utils"

interface FinalValueChartProps {
  readonly bins: readonly number[]
  readonly counts: readonly number[]
  readonly rawCounts: readonly number[]
  readonly binRanges: readonly { readonly min: number; readonly max: number }[]
  readonly annualConsumptionRate: number
  readonly annualConsumptionAmount: number
  readonly isUserDefinedConsumption: boolean
}

// Declared at module scope rather than inside FinalValueChart: a component defined in a
// render body is a new component type on every render, so React would unmount and remount
// the tooltip each time instead of updating it (react-hooks/static-components). Recharts
// clones the element passed as `content` and supplies `active` and `payload` itself.
const CustomTooltip = ({
  active,
  payload,
}: {
  readonly active?: boolean
  readonly payload?: readonly {
    readonly payload: {
      readonly binMin: number
      readonly binMax: number
      readonly percentage: number
      readonly rawCount: number
    }
  }[]
}) => {
  if (!active || !payload?.length) return null

  const data = payload[0].payload

  return (
    <div className="bg-white p-3 border rounded shadow">
      <div>
        <strong>Range:</strong> {formatCurrency(data.binMin)} - {formatCurrency(data.binMax)}
      </div>
      <div>
        <strong>Probability:</strong> {data.percentage.toFixed(1)}%
      </div>
      <div>
        <strong>Count:</strong> {data.rawCount} scenario{data.rawCount !== 1 ? "s" : ""}
      </div>
    </div>
  )
}

const FinalValueChart: React.FC<FinalValueChartProps> = ({
  bins,
  counts,
  rawCounts,
  binRanges,
  annualConsumptionRate,
  annualConsumptionAmount,
  isUserDefinedConsumption,
}) => {
  const { isMobile, isTablet } = useResponsive()
  // Create chart data from bins and counts
  const chartData = useMemo(() => {
    // Add validation
    if (bins.length === 0 || counts.length === 0 || bins.length !== binRanges.length) {
      return []
    }

    return bins
      .map((binCenter, index) => {
        const range = binRanges[index]

        return {
          binCenter,
          percentage: counts[index] || 0,
          rawCount: rawCounts[index] || 0,
          binMin: range.min,
          binMax: range.max,
          displayValue: formatCurrency(binCenter),
        }
      })
      .filter(Boolean) // Remove null entries
  }, [bins, counts, rawCounts, binRanges])

  // Create tick marks from bin boundaries with responsive intervals
  const xAxisTicks = useMemo(() => {
    if (binRanges.length === 0) return []

    // Get all unique boundary values
    const boundaries = new Set<number>()
    binRanges.forEach(range => {
      boundaries.add(range.min)
      boundaries.add(range.max)
    })

    // Convert to sorted array - include all boundaries
    const allTicks = Array.from(boundaries).sort((a, b) => a - b)

    // Calculate responsive tick interval
    let interval = 1
    if (isMobile) {
      // Mobile: Show fewer ticks (every 4th-6th tick)
      interval = Math.max(Math.ceil(allTicks.length / 4), 1)
    } else if (isTablet) {
      // Tablet: Show moderate number of ticks (every 2nd-3rd tick)
      interval = Math.max(Math.ceil(allTicks.length / 6), 1)
    } else {
      // Desktop: Show more ticks but still manageable
      interval = Math.max(Math.ceil(allTicks.length / 8), 1)
    }

    // Always include first and last tick, plus $0 if it exists
    const selectedTicks = []
    for (let i = 0; i < allTicks.length; i += interval) {
      selectedTicks.push(allTicks[i])
    }

    // Ensure the last tick is always included if not already
    const lastTick = allTicks[allTicks.length - 1]
    if (selectedTicks[selectedTicks.length - 1] !== lastTick) {
      selectedTicks.push(lastTick)
    }

    // Always include $0 if it exists in the data and isn't already selected
    const zeroTick = allTicks.find(tick => tick === 0)
    if (zeroTick !== undefined && !selectedTicks.includes(zeroTick)) {
      selectedTicks.push(zeroTick)
    }

    // Sort the final ticks to maintain proper order
    return selectedTicks.sort((a, b) => a - b)
  }, [binRanges, isMobile, isTablet])

  // Calculate domain for the chart (min and max of all boundaries)
  const chartDomain = useMemo(() => {
    if (binRanges.length === 0) return ["dataMin", "dataMax"]

    // Use the exact bin range extremes with no padding
    const minValue = binRanges[0]?.min
    const maxValue = binRanges[binRanges.length - 1]?.max

    return [minValue, maxValue]
  }, [binRanges])

  // Responsive formatting for currency labels
  const formatCurrencyResponsive = (value: number) => {
    if (isMobile) {
      // Mobile: Use abbreviated format
      if (Math.abs(value) >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`
      } else if (Math.abs(value) >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`
      }
      return formatCurrency(value)
    }
    return formatCurrency(value)
  }

  // Responsive chart margins and label angles
  const chartMargins = useMemo(() => {
    if (isMobile) {
      return { top: 20, right: 20, left: 20, bottom: 120 }
    } else if (isTablet) {
      return { top: 20, right: 25, left: 20, bottom: 110 }
    }
    return { top: 20, right: 30, left: 20, bottom: 100 }
  }, [isMobile, isTablet])

  const labelAngle = useMemo(() => {
    return isMobile ? -90 : -45
  }, [isMobile])

  const xAxisHeight = useMemo(() => {
    return isMobile ? 100 : 80
  }, [isMobile])

  const xAxisLabelOffset = useMemo(() => {
    return isMobile ? 15 : -20
  }, [isMobile])

  // Check if we have data to display - after all hooks are called
  if (bins.length === 0 || counts.length === 0 || chartData.length === 0) {
    return <div className="alert alert-info">No data available for histogram visualization.</div>
  }

  return (
    <div>
      {/* Display consumption rate info above the chart */}
      <div className="mb-2 text-muted">
        <small>
          Initial Annual Consumption Rate: {(annualConsumptionRate * 100).toFixed(2)}%
          <InfoPopover
            content={
              isUserDefinedConsumption
                ? "This rate is calculated from the Initial Consumption amount you specified in the General Parameters tab."
                : "<p>This rate is 1% above the fail-safe consumption rate determined when simulating all historical retirements, see the No Filters column in the Failure Rates results tab.</p><p>You can supply your own rate by providing an Initial Consumption amount in the General Parameters tab.</p>"
            }
            htmlContent={!isUserDefinedConsumption}
          />
          <span className="ms-4">
            Initial Annual Consumption: {formatCurrency(annualConsumptionAmount)}
          </span>
        </small>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={chartMargins}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="binCenter"
            type="number"
            scale="linear"
            domain={chartDomain}
            ticks={xAxisTicks}
            tickFormatter={formatCurrencyResponsive}
            label={{
              value: "Final Portfolio Value",
              position: "insideBottom",
              offset: xAxisLabelOffset,
              style: { textAnchor: "middle" },
            }}
            angle={labelAngle}
            textAnchor="end"
            interval={0}
            height={xAxisHeight}
          />
          <YAxis
            label={{
              value: "Probability",
              angle: -90,
              offset: -10,
              position: "insideLeft",
              style: { textAnchor: "middle" },
            }}
            tickFormatter={(value: number) => `${value.toFixed(1)}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="percentage" fill="#2780e3" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default FinalValueChart
